import { useEffect, useMemo, useState } from 'react'

import {
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import CancelConfirmButtons from 'Components/UI/Molecules/CancelConfirmButtons'
import InputsWithoutValidate from 'Components/UI/Molecules/InputsWithoutValidate'
import DialogComponent from 'Components/UI/Molecules/Modals/DialogComponent'

import useStyles from './styles'

const ORIGIN_ORDER = { ECO: 0, OEM: 1, DOS: 2 }
const DEFAULT_ORIGINS = ['ECO', 'OEM', 'DOS']

const toPointId = (value) => {
  if (value === '' || value === undefined || value === null) return null
  return Number.isNaN(Number(value)) ? value : Number(value)
}

const sameId = (a, b) => {
  if (a === null || b === null) return a === b
  return a?.toString() === b?.toString()
}

const normalizeRows = (rows) => {
  const byOrigin = {}

  ;(Array.isArray(rows) ? rows : []).forEach((row) => {
    const origin = (row?.origen || '').toString().trim().toUpperCase()
    if (ORIGIN_ORDER[origin] === undefined) return
    if (!origin) return
    byOrigin[origin] = {
      origen: origin,
      punto_venta_id: toPointId(row?.punto_venta_id),
    }
  })

  return Object.values(byOrigin).sort((a, b) => ORIGIN_ORDER[a.origen] - ORIGIN_ORDER[b.origen])
}

const getPointOptions = (rules, location, origin) => {
  if (!location || !origin || origin === 'DOS') return []
  const locationRules =
    rules?.[location.nombre] || rules?.[location.id] || rules?.[location.id?.toString()] || {}

  return (Array.isArray(locationRules[origin]) ? locationRules[origin] : [])
    .map((item) => {
      const id = toPointId(item?.id ?? item?.punto_venta_id)
      const name =
        item?.numero && item?.nombre ? `${item.numero} (${item.nombre})` : item?.nombre || ''
      if (id === null || !name) return null
      return { id, nombre: name }
    })
    .filter(Boolean)
}

const Location = ({ data, setData }) => {
  const classes = useStyles()

  const [openAdd, setOpenAdd] = useState(false)
  const [newRow, setNewRow] = useState({ origen: '', punto_venta_id: null })
  const [rowsByLocation, setRowsByLocation] = useState({})

  const locationFields = data?.ubicacion || data?.ubicacion_fields || {}
  const rules = useMemo(
    () => locationFields?.reglas_puntos_venta || data?.fields?.reglas_puntos_venta || {},
    [locationFields?.reglas_puntos_venta, data?.fields?.reglas_puntos_venta]
  )
  const locationOptions = locationFields?.ubicaciones || data?.fields?.ubicaciones || []
  const blockedLocations =
    locationFields?.ubicaciones_sin_punto_venta || data?.fields?.ubicaciones_sin_punto_venta || []

  const selectedLocationId = data?.data?.ubicacion_id || null
  const selectedLocation =
    locationOptions.find((item) => sameId(item.id, selectedLocationId)) ||
    data?.ubicacion?.actual ||
    null

  useEffect(() => {
    const key =
      data?.data?.ubicacion_id === null || data?.data?.ubicacion_id === undefined
        ? ''
        : data.data.ubicacion_id.toString()
    const initialRows = Array.isArray(data?.data?.puntos_venta_origen)
      ? data.data.puntos_venta_origen
      : []
    setRowsByLocation(key ? { [key]: initialRows } : {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data?.id])

  const rawRows = useMemo(() => {
    const key =
      selectedLocationId === null || selectedLocationId === undefined
        ? ''
        : selectedLocationId.toString()
    if (key && Array.isArray(rowsByLocation[key])) return rowsByLocation[key]
    return Array.isArray(data?.data?.puntos_venta_origen) ? data.data.puntos_venta_origen : []
  }, [rowsByLocation, selectedLocationId, data?.data?.puntos_venta_origen])

  const rows = useMemo(() => normalizeRows(rawRows), [rawRows])
  const usedOrigins = rows.map((row) => row.origen)

  const originOptions = DEFAULT_ORIGINS.map((origin) => ({
    id: origin,
    nombre: origin,
    disabled: usedOrigins.includes(origin),
  }))

  const pointOptions = useMemo(
    () => getPointOptions(rules, selectedLocation, newRow.origen),
    [rules, selectedLocation, newRow.origen]
  )

  const disabledByUserState = data?.data?.activado === 0
  const addDisabledByLocation = (Array.isArray(blockedLocations) ? blockedLocations : []).some(
    (item) =>
      item?.toString() === selectedLocation?.id?.toString() ||
      item?.toString() === selectedLocation?.nombre
  )

  const persistRows = (nextRows) => {
    const normalizedRaw = nextRows.map((row) => ({
      origen: row.origen,
      punto_venta_id: row.punto_venta_id,
    }))
    const key =
      selectedLocationId === null || selectedLocationId === undefined
        ? ''
        : selectedLocationId.toString()

    if (key) {
      setRowsByLocation((prev) => ({
        ...prev,
        [key]: normalizedRaw,
      }))
    }

    setData({
      ...data,
      data: {
        ...data.data,
        puntos_venta_origen: normalizedRaw,
      },
    })
  }

  const handleLocationChange = (location) => {
    const nextLocationId = location?.id || null
    const currentKey =
      selectedLocationId === null || selectedLocationId === undefined
        ? ''
        : selectedLocationId.toString()
    const nextKey =
      nextLocationId === null || nextLocationId === undefined ? '' : nextLocationId.toString()

    setRowsByLocation((prev) => {
      const next = { ...prev }
      if (currentKey) next[currentKey] = Array.isArray(rawRows) ? rawRows : []
      if (nextKey && !Array.isArray(next[nextKey])) next[nextKey] = []
      return next
    })

    setData({
      ...data,
      data: {
        ...data.data,
        ubicacion_id: nextLocationId,
        puntos_venta_origen:
          nextKey && Array.isArray(rowsByLocation[nextKey]) ? rowsByLocation[nextKey] : [],
      },
    })
  }

  const removeOrigin = (origin) => {
    persistRows(rows.filter((row) => row.origen !== origin))
  }

  const addRow = () => {
    persistRows(
      [...rows, { origen: newRow.origen, punto_venta_id: newRow.punto_venta_id }].sort(
        (a, b) => ORIGIN_ORDER[a.origen] - ORIGIN_ORDER[b.origen]
      )
    )

    setOpenAdd(false)
    setNewRow({ origen: '', punto_venta_id: null })
  }

  const canAddPoint =
    !addDisabledByLocation &&
    !disabledByUserState &&
    originOptions.some((option) => option.disabled === false)

  const disableAddSubmit =
    !newRow.origen || pointOptions.length === 0 || newRow.punto_venta_id === null

  const formFields = [
    {
      class: 'autocomplete',
      label: 'Sucursal',
      name: 'ubicacion_id',
      value: selectedLocationId,
      options: locationOptions,
      optionLabel: `options["nombre"]`,
      optionValue: 'id',
      onChange: (value) => handleLocationChange(value),
      sm: 6,
      xs: 12,
      disabled: disabledByUserState,
      forceSelect: true,
    },
  ]

  const addFormFields = [
    {
      class: 'textfield',
      label: 'Sucursal',
      name: 'sucursal',
      value: selectedLocation?.nombre || '',
      sm: 12,
      xs: 12,
      disabled: true,
    },
    {
      class: 'autocomplete',
      label: 'Origen',
      name: 'origen',
      value: newRow.origen,
      options: originOptions,
      optionLabel: `options["nombre"]`,
      optionValue: 'id',
      getOptionDisabled: (option) => option.disabled,
      disabledProp: 'disabled',
      onChange: (value) => setNewRow({ origen: value?.id || '', punto_venta_id: null }),
      sm: 12,
      xs: 12,
      forceSelect: true,
    },
    {
      class: 'autocomplete',
      label: 'Punto de venta',
      name: 'punto_venta_id',
      value: newRow.punto_venta_id,
      options: pointOptions,
      optionLabel: `options["nombre"]`,
      optionValue: 'id',
      onChange: (value) => setNewRow((prev) => ({ ...prev, punto_venta_id: value?.id ?? null })),
      sm: 12,
      xs: 12,
      disabled: newRow.origen === 'DOS' || !newRow.origen || pointOptions.length === 0,
      forceSelect: true,
    },
  ]

  return (
    <div className={classes.root}>
      <Grid container>
        <Grid item xs={12}>
          <InputsWithoutValidate fields={formFields} spacing={2} />
        </Grid>

        <Grid item xs={12}>
          <div className={classes.tableWrap}>
            <Paper>
              <Table size="small">
                <TableHead>
                  <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell style={{ width: '35%' }}>ORIGEN</TableCell>
                    <TableCell>PUNTO DE VENTA</TableCell>
                    <TableCell className={classes.rowActions}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const label =
                      row.origen === 'DOS'
                        ? ''
                        : getPointOptions(rules, selectedLocation, row.origen).find((item) =>
                            sameId(item.id, row.punto_venta_id)
                          )?.nombre || ''

                    return (
                      <TableRow key={row.origen}>
                        <TableCell>{row.origen}</TableCell>
                        <TableCell>{label}</TableCell>
                        <TableCell className={classes.rowActions}>
                          <IconButton
                            onClick={() => removeOrigin(row.origen)}
                            disabled={disabledByUserState}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )
                  })}

                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        Sin puntos de venta asignados
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          </div>
        </Grid>

        <Grid item xs={12} className={classes.addButtonWrap}>
          <IconButton color="primary" onClick={() => setOpenAdd(true)} disabled={!canAddPoint}>
            <AddIcon />
          </IconButton>
        </Grid>
      </Grid>

      {openAdd && (
        <DialogComponent
          open={openAdd}
          handleclose={() => {
            setOpenAdd(false)
            setNewRow({ origen: '', punto_venta_id: null })
          }}
          title="Agregar punto venta"
          disableBackdropClick
          disableEscapeKeyDown
          maxHeight
          children={<InputsWithoutValidate fields={addFormFields} spacing={2} />}
          actions={
            <CancelConfirmButtons
              onClickCancel={() => {
                setOpenAdd(false)
                setNewRow({ origen: '', punto_venta_id: null })
              }}
              onClickSubmit={addRow}
              submitText="Agregar"
              disabledSubmit={disableAddSubmit}
            />
          }
        />
      )}
    </div>
  )
}

export default Location
